import { useEffect, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import "./Logbooks.css";

export default function HematologyLogs() {
  const baseURL = process.env.REACT_APP_BASE_URL;
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [employees, setEmployees] = useState([]);

  const [formData, setFormData] = useState({
    log_date: "",
    patient_id_number: "",
    patient_name: "",
    patient_age: "",
    patient_sex: "",
    analyst_id: "",
    impression_of_chromatogram: "",
    consultant_id: "",
    remarks: "",
  });

  const token = localStorage.getItem("token");

  // ✅ Axios instance with token
  const api = axios.create({
    baseURL: `${baseURL}`,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  // ✅ Handle token expiry
  api.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        toast.error("Session expired. Redirecting to login...");
        localStorage.removeItem("token");
        window.location.href = "/login";
      }
      return Promise.reject(error);
    }
  );

  // ✅ Fetch all logs and employees
  useEffect(() => {
    fetchLogs();
    fetchEmployees();
  }, []);

  const fetchLogs = async () => {
    try {
      const response = await api.get("/hematology_logs");
      setLogs(response.data);
    } catch (error) {
      console.error("Error fetching hematology logs:", error);
      alert("Error fetching hematology logs. Check console for details.");
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.get("/employees");
      setEmployees(response.data);
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // ✅ Add new hematology log
  const handleAdd = async (e) => {
    e.preventDefault();

    // Ensure analyst and consultant are not same
    if (formData.analyst_id === formData.consultant_id) {
      alert("Analyst and Consultant cannot be the same person!");
      return;
    }

    try {
      // Convert IDs to integers
      const payload = {
        ...formData,
        analyst_id: parseInt(formData.analyst_id),
        consultant_id: parseInt(formData.consultant_id),
        patient_age: parseInt(formData.patient_age),
      };

      await api.post("/hematology_logs", payload);
      setShowForm(false);
      setFormData({
        log_date: "",
        patient_id_number: "",
        patient_name: "",
        patient_age: "",
        patient_sex: "",
        analyst_id: "",
        impression_of_chromatogram: "",
        consultant_id: "",
        remarks: "",
      });
      fetchLogs();
      toast.success("Hematology log added successfully!");
    } catch (error) {
      console.error("Error adding hematology log:", error);
      alert("Failed to add hematology log entry. Check console for details.");
    }
  };

  // ✅ Export to Excel
  const exportToExcel = () => {
    if (logs.length === 0) {
      alert("No data available to export.");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(logs);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "HematologyLogs");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, "HematologyLogs.xlsx");
  };

  return (
    <div className="logbooks-layout">
      {/* <Sidebar /> */}

      <div className="logbooks-container">
        <div className="logbooks-header">
          <h2>Hematology Logs</h2>
          <div className="header-actions">
            <button className="export-btn" onClick={exportToExcel}>
              Export to Excel
            </button>
            <button className="add-btn" onClick={() => setShowForm(true)}>
              + Add
            </button>
          </div>
        </div>

        {loading ? (
          <p>Loading hematology logs...</p>
        ) : (
          <table className="logbooks-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Date</th>
                <th>Patient ID</th>
                <th>Name</th>
                <th>Age</th>
                <th>Sex</th>
                <th>Analyst</th>
                <th>Impression</th>
                <th>Consultant</th>
                <th>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {logs.length > 0 ? (
                logs.map((log) => (
                  <tr key={log.id}>
                    <td>{log.id}</td>
                    <td>{log.log_date}</td>
                    <td>{log.patient_id_number}</td>
                    <td>{log.patient_name}</td>
                    <td>{log.patient_age}</td>
                    <td>{log.patient_sex}</td>
                    <td>{log.analyst_name}</td>
                    <td>{log.impression_of_chromatogram}</td>
                    <td>{log.consultant_name}</td>
                    <td>{log.remarks}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" style={{ textAlign: "center" }}>
                    No records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}

        {/* ✅ Add Modal Form */}
        {showForm && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>Add Hematology Log</h3>
              <form onSubmit={handleAdd}>
                <input
                  type="date"
                  name="log_date"
                  value={formData.log_date}
                  onChange={handleChange}
                  required
                />

                <input
                  type="text"
                  name="patient_id_number"
                  placeholder="Patient ID Number"
                  value={formData.patient_id_number}
                  onChange={handleChange}
                  required
                />

                <input
                  type="text"
                  name="patient_name"
                  placeholder="Patient Name"
                  value={formData.patient_name}
                  onChange={handleChange}
                  required
                />

                <input
                  type="number"
                  name="patient_age"
                  placeholder="Patient Age"
                  value={formData.patient_age}
                  onChange={handleChange}
                  required
                />

                <select
                  name="patient_sex"
                  value={formData.patient_sex}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Sex</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>

                {/* Analyst dropdown */}
                <select
                  name="analyst_id"
                  value={formData.analyst_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Analyst</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.comp_name}
                    </option>
                  ))}
                </select>

                <input
                  name="impression_of_chromatogram"
                  placeholder="Impression of Chromatogram"
                  value={formData.impression_of_chromatogram}
                  onChange={handleChange}
                  required
                />

                {/* Consultant dropdown */}
                <select
                  name="consultant_id"
                  value={formData.consultant_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Consultant</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.comp_name}
                    </option>
                  ))}
                </select>

                <input
                  name="remarks"
                  placeholder="Remarks / Comments"
                  value={formData.remarks}
                  onChange={handleChange}
                  required
                />

                <div className="modal-actions">
                  <button type="submit" className="save-btn">
                    Save
                  </button>
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={() => setShowForm(false)}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}